/**
 * ⚠️ DUPLICATE COMPONENT WARNING ⚠️
 *
 * This component (TypeaheadOverlay) is the LEGACY version of a duplicated component.
 * The new version is ResultDropdown located at:
 * `/packages/mirage/packages/mosaics/Omnibox/components/ResultDropdown/ResultDropdown.tsx`
 *
 * REQUIRED ACTION FOR DEVELOPERS:
 * Any changes made to this file MUST be mirrored in the other file to maintain
 * consistent behavior across both implementations.
 *
 * WHY THIS MATTERS:
 * These components serve the same purpose but are conditionally used based on the
 * feature flag: `dash_2025_03_18_omnibox`
 *
 * Failing to synchronize changes will create inconsistent user experiences
 * depending on whether a user has the feature flag enabled or disabled.
 */

import "./variables.module.css";

import type { OverlayProps } from "@dropbox/dig-components/overlay";
import { Overlay } from "@dropbox/dig-components/overlay";
import { TypeaheadKeyboardShortcutBanner } from "@mirage/education/TypeaheadKeyboardShortcutBanner";
import { OmniboxResultRowAdapter } from "@mirage/mosaics/Omnibox/components/ResultRow/adapter/OmniboxResultRowAdapter";
import {
  showAddToStackShortcut,
  showCopyLinkShortcut,
  showNewShortcut,
  showSummarizeShortcut,
} from "@mirage/mosaics/SearchBarWithTypeahead/util/util";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { tagged } from "@mirage/service-logging";
import i18n from "@mirage/translations";
import classnames from "classnames";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useScrollToTypeaheadItem } from "../hooks/useScrollToTypeaheadItem";
import { TypeaheadConnectorActivation } from "./TypeaheadConnectorActivation";
import style from "./TypeaheadOverlay.module.css";
import TypeaheadResultRow from "./TypeaheadResultRow";

import { ModeAtom } from "@mirage/mosaics/Omnibox/atoms/mode";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import type { ActivationTypes } from "@mirage/service-connectors/useActivationForConnectors";
import { isInsideExtensionSidePanel } from "@mirage/shared/util/webExtensionsHelpers";
import { useAtomValue } from "jotai";
import { throttle } from "lodash";
import { NoSearchResults } from "../General/NoSearchResults";

type Props = {
  open?: boolean;
  anchor: React.RefObject<HTMLElement>;
  query: string;
  results?: Array<TypeaheadResult>;
  selectedIdx?: number;
  /** called with whatever index the user currently has their cursor hovered over */
  setHoveredIdx: (i: number | null) => void;
  onResultClicked: (item: TypeaheadResult, index: number) => void;
  onListItemView?: (item: TypeaheadResult, index: number) => void;
  isMouseActive?: boolean;
  activationType?: ActivationTypes;
  children?: React.ReactNode;
  fullWidth?: boolean;
  setTypeaheadOpen?: (open: boolean) => void;
  disableActivation?: boolean;
  isAugustStartPageEnabled?: boolean;
  isOmniboxEnabled?: boolean;
  isSlimMode?: boolean;
  showShortcutsBanner?: boolean;
};

const logger = tagged("typeahead-overlay");

export default function TypeaheadOverlay({
  open = true,
  anchor,
  results = [],
  selectedIdx = 0,
  setHoveredIdx,
  onResultClicked,
  query = "",
  onListItemView,
  isMouseActive = true,
  activationType,
  children,
  fullWidth = false,
  setTypeaheadOpen,
  disableActivation = false,
  isAugustStartPageEnabled = false,
  isOmniboxEnabled = false,
  isSlimMode = false,
  showShortcutsBanner = true,
}: Props) {
  const isDarwin = EnvCtx?.platform === "darwin";
  const paragraphObserver = useRef<IntersectionObserver>();
  const visibleResultsRef = useRef<Set<string>>(new Set());
  const { resultItemRefs, overlayContainerRef } = useScrollToTypeaheadItem(
    selectedIdx,
    results,
  );
  const { isDualModeLauncher } = useDualMode();
  const omniboxScopedMode = useAtomValue(ModeAtom);

  const listRef = useRef<HTMLUListElement>(null);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<number | null>(null);

  // Recalculate height when overlay opens or window resizes
  useLayoutEffect(() => {
    if (!open || !isOmniboxEnabled || isDualModeLauncher) return;

    // Calculate the available height for the list
    const calculateMaxHeight = throttle(() => {
      if (!listRef.current || !open) return;
      const rect = listRef.current.getBoundingClientRect();
      const buffer = 60; // buffer from bottom of viewport
      const availableHeight = window.innerHeight - rect.top - buffer;

      logger.debug(
        `Calculated available height: ${availableHeight}px with buffer: ${buffer}px`,
      );

      if (availableHeight >= 150) {
        setDynamicMaxHeight(availableHeight);
      }
    }, 50); // throttle time

    calculateMaxHeight();

    window.addEventListener("resize", calculateMaxHeight);
    return () => {
      window.removeEventListener("resize", calculateMaxHeight);
      calculateMaxHeight.cancel();
    };
  }, [isDualModeLauncher, isOmniboxEnabled, open]);

  useEffect(() => {
    if (onListItemView === undefined) return;
    if (!open) {
      // Clear visible results in order to ensure that we are logging visible results properly
      visibleResultsRef.current = new Set();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const nVisibleResults: string[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const { target } = entry;
            const entryUuid = target.getAttribute("data-x-uuid");
            if (entryUuid) {
              nVisibleResults.push(entryUuid);
              if (!visibleResultsRef.current.has(entryUuid)) {
                const index = results.findIndex((r) => r.uuid === entryUuid);
                if (index >= 0) {
                  const item = results[index];
                  onListItemView(item, index);
                }
              }
            }
          }
        });
        visibleResultsRef.current = new Set([
          ...visibleResultsRef.current,
          ...nVisibleResults,
        ]);
      },
      { threshold: 0.75 },
    );
    paragraphObserver.current = observer;

    return () => {
      paragraphObserver.current = undefined;
    };
  }, [results, open, onListItemView]);

  // Inline the results in the Dual Mode Launcher
  const Container = useMemo(
    () =>
      isDualModeLauncher || isInsideExtensionSidePanel()
        ? (props: React.HTMLAttributes<HTMLDivElement>) => (
            <div
              {...props}
              className={classnames(style.omniboxLauncher, props.className)}
            />
          )
        : (props: Partial<OverlayProps>) => (
            <Overlay
              placement="bottom"
              anchorRef={anchor}
              setWidthSameAsAnchor={true}
              boundaryRef={overlayContainerRef}
              {...props}
            />
          ),
    [isDualModeLauncher, anchor, overlayContainerRef],
  );

  return (
    <>
      {open && (
        <div ref={overlayContainerRef}>
          <Container
            className={classnames(style.overlay, {
              [style.omnibox]: isOmniboxEnabled,
              [style.omniboxFull]: isOmniboxEnabled && !isSlimMode,
              [style.omniboxSlim]: isOmniboxEnabled && isSlimMode,
              [style.opened__deprecated]: open && !isOmniboxEnabled,
              [style.opened]: open && isOmniboxEnabled,
              [style.activationOverlay__deprecated]:
                activationType && !isOmniboxEnabled,
              [style.activationOverlay]: activationType && isOmniboxEnabled,
              [style.fullWidthSearchbar]: fullWidth,
              [style.augustRevision]: isAugustStartPageEnabled,
            })}
          >
            {children}
            <ul
              ref={listRef}
              className={classnames(style.listContainer, {
                [style.animateFadeIn]: open,
                [style.isDualModeLauncher]: isDualModeLauncher,
                [style.omnibox]: isOmniboxEnabled,
                [style.__deprecated]: !isOmniboxEnabled,
              })}
              style={
                dynamicMaxHeight
                  ? { maxHeight: `${dynamicMaxHeight}px` }
                  : undefined
              }
              role="listbox"
              aria-label={i18n.t("aria_search_results_list")}
            >
              {results.map((result, i) => {
                if (!isOmniboxEnabled) {
                  return (
                    <TypeaheadResultRow
                      result={result}
                      key={result.uuid}
                      resultRowIndex={i}
                      isSelected={i === selectedIdx}
                      setIsHovered={() => {
                        setHoveredIdx(i);
                      }}
                      setIsUnhovered={() => setHoveredIdx(null)}
                      onClick={() => {
                        onResultClicked(result, i);
                      }}
                      typeaheadQuery={query}
                      ref={resultItemRefs[i]}
                      observer={paragraphObserver.current}
                      isMouseActive={isMouseActive}
                      fullWidth={fullWidth}
                    />
                  );
                }

                // NOTE: Leaving this for Alan to finalize implementation.
                // This approach just uses a flat list, consistent with deprecated typeahead,
                // and detects the start of sections
                // Then, inserts a <li> with aria-hidden=true to separate sections.
                const isFirstInSection =
                  i === 0 ||
                  results[i - 1].scoredResult.sectionId !==
                    result.scoredResult.sectionId;

                return (
                  <OmniboxResultRowAdapter
                    result={result}
                    key={result.uuid}
                    isFirstInSection={isFirstInSection}
                    resultRowIndex={i}
                    isSelected={i === selectedIdx}
                    setIsHovered={() => {
                      setHoveredIdx(i);
                    }}
                    setIsUnhovered={() => setHoveredIdx(null)}
                    onClick={() => {
                      onResultClicked(result, i);
                    }}
                    typeaheadQuery={query}
                    ref={resultItemRefs[i]}
                    observer={paragraphObserver.current}
                    isMouseActive={isMouseActive}
                    fullWidth={fullWidth}
                  />
                );
              })}
              {!disableActivation && activationType && (
                <TypeaheadConnectorActivation
                  activationType={activationType}
                  setTypeaheadOpen={setTypeaheadOpen}
                />
              )}
            </ul>
            {results.length === 0 && isDualModeLauncher && (
              <NoSearchResults query={query} />
            )}
            {showShortcutsBanner && (
              <div className={style.banner}>
                <TypeaheadKeyboardShortcutBanner
                  isDarwin={isDarwin}
                  showAddToStackShortcut={showAddToStackShortcut(
                    results[selectedIdx],
                  )}
                  showSummarizeShortcut={showSummarizeShortcut(
                    results[selectedIdx],
                  )}
                  showCopyLink={showCopyLinkShortcut(
                    isOmniboxEnabled,
                    results[selectedIdx],
                  )}
                  showNewShortcut={showNewShortcut(
                    isOmniboxEnabled,
                    omniboxScopedMode,
                  )}
                />
              </div>
            )}
          </Container>
        </div>
      )}
    </>
  );
}
